import React, { useState, useEffect, useCallback, useRef } from 'react';
import cn from 'classnames';
import styles from './Teams.module.sass';
import Card from '../../components/Card';
import Icon from '../../components/Icon';
import ModalTeams from '../../components/ModalTeams';
import Row from './Teams/Row';
import { getUsers, createMember, updateUser, deleteUser, USER_EVENTS } from '../../services/user.service';
import { showSuccess, showError } from '../../services/toast.service';
import eventEmitter from '../../services/event.service';

const Member = ({ className }) => {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [editingMember, setEditingMember] = useState(null);

    const fetchMembers = useCallback(async () => {
        try {
            setLoading(true);
            const usersData = await getUsers();
            setMembers(usersData);
        } catch (error) {
            console.error("Failed to fetch users:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    // Références pour les fonctions de callback des événements
    const eventCallbacks = useRef({
        handleMemberCreated: null,
        handleMemberUpdated: null,
        handleMemberDeleted: null
    });

    useEffect(() => {
        fetchMembers();
        
        // Définir les fonctions de callback
        eventCallbacks.current.handleMemberCreated = (newMember) => {
            console.log('Membre créé événement reçu:', newMember);
            setMembers(prevMembers => [...prevMembers, newMember]);
        };
        
        eventCallbacks.current.handleMemberUpdated = (updatedMember) => {
            console.log('Membre mis à jour événement reçu:', updatedMember);
            setMembers(prevMembers => 
                prevMembers.map(member => 
                    member.id === updatedMember.id ? updatedMember : member
                )
            );
        };
        
        eventCallbacks.current.handleMemberDeleted = (deletedMemberId) => {
            console.log('Membre supprimé événement reçu:', deletedMemberId);
            setMembers(prevMembers => 
                prevMembers.filter(member => member.id !== deletedMemberId)
            );
        };
        
        // S'abonner aux événements
        eventEmitter.subscribe(USER_EVENTS.CREATED, eventCallbacks.current.handleMemberCreated);
        eventEmitter.subscribe(USER_EVENTS.UPDATED, eventCallbacks.current.handleMemberUpdated);
        eventEmitter.subscribe(USER_EVENTS.DELETED, eventCallbacks.current.handleMemberDeleted);
        
        // Nettoyage lors du démontage du composant
        return () => {
            eventEmitter.unsubscribe(USER_EVENTS.CREATED, eventCallbacks.current.handleMemberCreated);
            eventEmitter.unsubscribe(USER_EVENTS.UPDATED, eventCallbacks.current.handleMemberUpdated);
            eventEmitter.unsubscribe(USER_EVENTS.DELETED, eventCallbacks.current.handleMemberDeleted);
        };
    }, [fetchMembers]);

    const handleOpenModal = (member = null) => {
        setEditingMember(member);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingMember(null);
    };

    const handleSaveMember = async (memberData) => {
        console.log('[Member/index] handleSaveMember déclenché avec:', memberData);
        setIsSubmitting(true);
        try {
            if (editingMember) {
                console.log('-> Tentative de mise à jour de l\'utilisateur...');
                await updateUser(editingMember.id, memberData);
                showSuccess('Utilisateur mis à jour avec succès.');
                // La mise à jour de l'interface se fait automatiquement via l'événement USER_EVENTS.UPDATED
                handleCloseModal();
            } else {
                console.log('-> Tentative de création de l\'utilisateur...');
                await createMember(memberData);
                showSuccess('Utilisateur créé avec succès.');
                // L'ajout à l'interface se fait automatiquement via l'événement USER_EVENTS.CREATED
                handleCloseModal();
            }
        } catch (error) {
            console.error("Échec de la sauvegarde du membre:", error);
            
            // Gestion spécifique des erreurs d'email en double
            if (error.message && (
                error.message.includes('email existe déjà') || 
                error.message.includes('email already exists') || 
                error.message.includes('Un utilisateur avec cet email existe')
            )) {
                showError('Un utilisateur avec cette adresse email existe déjà.');
            } else if (error.message) {
                // Afficher le message d'erreur exact
                showError(error.message);
            } else {
                showError("Échec de la sauvegarde du membre.");
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteMember = async (memberId) => {
        try {
            await deleteUser(memberId);
            showSuccess('Utilisateur supprimé avec succès.');
            // La suppression de l'interface se fait automatiquement via l'événement USER_EVENTS.DELETED
        } catch (error) {
            console.error("Échec de la suppression de l'utilisateur:", error);
            showError('Erreur lors de la suppression de l\'utilisateur.');
        }
    };

    const handleStatusChange = async (member, newStatus) => {
        console.log('🔄 handleStatusChange appelé:', { member, newStatus });
        try {
            // Comme nous récupérons directement les users, l'ID est member.id
            const userId = member.id;
            console.log('👤 userId:', userId);
            
            if (!userId) {
                throw new Error('ID utilisateur non trouvé');
            }
            
            console.log('📤 Mise à jour du statut:', { userId, status: newStatus });
            await updateUser(userId, { status: newStatus });
            showSuccess('Statut mis à jour avec succès.');
            // La mise à jour de l'interface se fait automatiquement via l'événement USER_EVENTS.UPDATED
        } catch (error) {
            console.error("Échec de la mise à jour du statut:", error);
            showError('Erreur lors de la mise à jour du statut.');
        }
    };

    return (
        <>
            <div className={styles.shop}>
                <div className={styles.background}>
                    <img src="/images/content/bg-shop.jpg" alt="Background" />
                </div>

                <Card
                    className={cn(styles.card, className)}
                    title="Utilisateurs"
                    classTitle="title-blue"
                    head={
                      <button className={cn("button-stroke button-small", styles.button)} onClick={() => handleOpenModal()}>
                          <span>Ajouter un utilisateur</span>
                      </button>
                  }
                >
                    <div className={styles.wrapper}>
                        {loading ? (
                            <div className={styles.loading}> 
                                <div className="spinner-large" />
                            </div>
                        ) : members.length === 0 ? (
                            <div className={styles.empty}>Aucun membre trouvé.</div>
                        ) : (
                            <div className={styles.table}>
                                <div className={styles.row}>
                                    <div className={styles.col}>Membre</div>
                                    <div className={styles.col}>Entité</div>
                                    <div className={styles.col}>Rôle</div>
                                    <div className={styles.col}>Statut</div>
                                    <div className={styles.col}>Actions</div>
                                </div>
                                {members.map((member) => (
                                    <Row
                                        item={member}
                                        key={member.id}
                                        onEdit={() => handleOpenModal(member)}
                                        onDelete={() => handleDeleteMember(member.id)}
                                        onStatusChange={handleStatusChange}
                                    />
                                ))}
                            </div>
                        )}
                    </div>

                    {isModalOpen && (
                        <ModalTeams
                            visible={isModalOpen}
                            onClose={handleCloseModal}
                            onSave={handleSaveMember}
                            member={editingMember}
                            isSubmitting={isSubmitting}
                        />
                    )}
                </Card>
            </div>
        </>
    );
};

export default Member;